export default class BenMarcCanvasTexture {
    constructor(width, height) {
        this.canvas = document.createElement('canvas');
        this.ctx = this.canvas.getContext('2d');
        this.fillBlack();
        this.angle1 = 23;
        this.angle2 = 25;

        this.width = width / 4;
        this.height = height / 4;
        this.canvas.width = this.width;
        this.canvas.height = this.height;
        this.spacing = 130 / 4;
        this.spacing2 = 60 / 4;
        this.thickness = 70 / 4;
        this.lines = [];
        this.lineIndex = 0;
        this.currentLineIndex = 0;
        this.speed = 75;
        this.acceleration = 4; // 7
        // this.speed = 30 * 75;
        // this.speed = 75;

        this.drawDiagonalLines();
    }

    getNormalizedSpeed(delta) {
        return this.speed * delta;
    }

    fillBlack() {
        this.ctx.fillStyle = '#000000';
        this.ctx.fillRect(0, 0, this.width, this.height);
    }

    drawDiagonalLines() {
        let tanAngle = Math.tan(this.angle1 * Math.PI / 180);

        for (let x = -this.thickness / 2; x <= this.width; x += this.spacing) {
            let y1 = - this.thickness / 2;
            let x1 = x;
            let y2 = this.height + this.thickness / 2;
            let x2 = x - this.height / tanAngle - this.thickness / 2;

            if (x2 < 0) {
                y2 = this.height + x2 * tanAngle + this.thickness / 2;
                x2 = -this.thickness / 2;
            }

            if(this.lineIndex % 2 === 0) {
                this.lines.push({ x1, y1, x2, y2, thickness: this.thickness, length: 0 });
            } else {
                this.lines.push({ x1: x2, y1: y2, x2: x1, y2: y1, thickness: this.thickness, length: 0 });
            }

            this.lineIndex++;
        }

        tanAngle = Math.tan(this.angle2 * Math.PI / 180);

        for (let y = -this.thickness / 2; y <= this.height + this.thickness / 2; y += this.spacing2) {
            let x1 = this.width + this.thickness / 2;
            let y1 = y;
            let x2 = -this.thickness / 2;
            let y2 = y;
            let x3 = x2;
            let y3 = y1 + (this.width + this.thickness / 2 ) * tanAngle;

            if (y3 > this.height + this.thickness / 2) {
                const m1 = (y3 - y1) / (x3 - x1);
                const b1 = y1 - m1 * x1;

                const yF = this.height + this.thickness / 2;
                const xF = (yF - b1) / m1;

                x2 = xF;
                y2 = yF;
            } else {
                x2 = x3;
                y2 = y3;
            }

            if(this.lineIndex % 2 === 0) {
                this.lines.push({ x1, y1, x2, y2, thickness: this.thickness, length: 0 });
            } else {
                this.lines.push({ x1: x2, y1: y2, x2: x1, y2: y1, thickness: this.thickness, length: 0 });
            }

            this.lineIndex++;
        }
    }

    animate(delta) {
        let needsUpdate = false;
        if (this.currentLineIndex < this.lines.length) {
            needsUpdate = true;
            let line = this.lines[this.currentLineIndex];
            const totalLength = Math.sqrt((line.x2 - line.x1) ** 2 + (line.y2 - line.y1) ** 2);
            if (line.length < totalLength) {
                this.drawLine(line.x1, line.y1, line.x2, line.y2, line.thickness, line.length);
                line.length += this.getNormalizedSpeed(delta) + (this.currentLineIndex * this.acceleration);
                // line.length += this.getNormalizedSpeed(delta);
            } else {
                this.drawLine(line.x1, line.y1, line.x2, line.y2, line.thickness, totalLength);
                this.currentLineIndex++;
            }
        }

        return needsUpdate;
    }

    drawLine(x1, y1, x2, y2, thickness, length) {
        const totalLength = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);

        // Calculate the angle of rotation
        const angle = Math.atan2(y2 - y1, x2 - x1);

        // Create a gradient for this specific line
        const createGradient = (startX, startY, endX, endY) => {
            // Calculate the midpoint of the line
            const midX = (startX + endX) / 2;
            const midY = (startY + endY) / 2;

            // Calculate gradient start and end points (perpendicular to the line)
            const perpendicularAngle = angle + Math.PI / 2;  // Rotate by 90 degrees
            const gradientLength = thickness / 2;
            const gradientStartX = midX + Math.cos(perpendicularAngle) * gradientLength;
            const gradientStartY = midY + Math.sin(perpendicularAngle) * gradientLength;
            const gradientEndX = midX - Math.cos(perpendicularAngle) * gradientLength;
            const gradientEndY = midY - Math.sin(perpendicularAngle) * gradientLength;

            const gradient = this.ctx.createLinearGradient(
                gradientStartX, gradientStartY,
                gradientEndX, gradientEndY
            );

            // Check the direction of the line
            if (x2 < x1 || (x2 === x1 && y2 < y1)) {
                // Line is drawn right to left or bottom to top
                gradient.addColorStop(0, 'white');
                gradient.addColorStop(0.8, 'white');
                gradient.addColorStop(1, 'black');
            } else {
                // Line is drawn left to right or top to bottom
                gradient.addColorStop(0, 'black');
                gradient.addColorStop(0.2, 'white');
                gradient.addColorStop(1, 'white');
            }

            return gradient;
        };

        this.ctx.lineWidth = thickness;

        if (length >= totalLength) {
            this.ctx.strokeStyle = createGradient(x1, y1, x2, y2);
            this.ctx.beginPath();
            this.ctx.moveTo(x1, y1);
            this.ctx.lineTo(x2, y2);
            this.ctx.stroke();
        } else {
            const ratio = length / totalLength;
            const xPartial = x1 + (x2 - x1) * ratio;
            const yPartial = y1 + (y2 - y1) * ratio;

            this.ctx.strokeStyle = createGradient(x1, y1, xPartial, yPartial);
            this.ctx.beginPath();
            this.ctx.moveTo(x1, y1);
            this.ctx.lineTo(xPartial, yPartial);
            this.ctx.stroke();
        }
    }
}
